import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Breadcrumbs,
  Link as MuiLink,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { ReportsNavigation } from './ReportsNavigation';
import { ReportsViewer } from './ReportsViewer';

export const ReportsList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Extract file path from URL: /reports/path/to/file.md
  const filePath = location.pathname.startsWith('/reports/')
    ? decodeURIComponent(location.pathname.slice('/reports/'.length))
    : undefined;

  const handleFileSelect = (newFilePath: string) => {
    // Navigate to the new file path
    navigate(`/reports/${encodeURIComponent(newFilePath)}`, {
      replace: false,
    });
  };

  // Build breadcrumbs from file path
  const breadcrumbs = filePath
    ? filePath.split('/').map((segment, index, array) => {
        const path = array.slice(0, index + 1).join('/');
        const isLast = index === array.length - 1;
        return (
          <MuiLink
            key={path}
            component={isLast ? 'span' : Link}
            to={
              isLast
                ? undefined
                : `/reports/${encodeURIComponent(path)}`
            }
            variant="body2"
            sx={{
              cursor: isLast ? 'default' : 'pointer',
              color: isLast ? 'text.primary' : 'primary.main',
              textDecoration: isLast ? 'none' : 'underline',
            }}
          >
            {segment}
          </MuiLink>
        );
      })
    : [];

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Investigation Reports
        </Typography>
        {breadcrumbs.length > 0 && (
          <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
            <MuiLink
              component={Link}
              to="/reports"
              variant="body2"
              sx={{ cursor: 'pointer', color: 'primary.main' }}
            >
              Reports
            </MuiLink>
            {breadcrumbs}
          </Breadcrumbs>
        )}
      </Box>
      <Grid container sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Grid
          item
          xs={3}
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
            height: '100%',
          }}
        >
          <Paper
            sx={{
              height: '100%',
              borderRadius: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
            elevation={0}
          >
            <Box
              sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Reports
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              <ReportsNavigation
                onFileSelect={handleFileSelect}
                selectedPath={filePath}
              />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={9} sx={{ overflow: 'auto', height: '100%' }}>
          {filePath ? (
            <ReportsViewer
              filePath={filePath}
              currentPath={filePath}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body1">
                Select a report from the navigation to view it
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
